import * as React from 'react';

import type { CohortAudience } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { AUDIENCE_LEARNERS_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortLearners } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearners';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';

const SEARCH_DEBOUNCE_MS = 300;

type Params = {
  cohortAudience?: CohortAudience;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  filteredCount: number;
  loading: boolean;
  error: boolean;
};

/**
 * Server-backed learner list for the NLQ cohort flow. Uses
 * `fetchEnterpriseLearnerCohorts` for pagination and server-side search via
 * `searchString`. Exposes the same shape as `useAudienceLearnerList` so the
 * screen can swap sources.
 */
export const useCohortLearnerList = ({ cohortAudience }: Params): Result => {
  const [searchQuery, setSearchQueryRaw] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { learners, hasMore, loading, error } = useCohortLearners({
    cohortAudience,
    page,
    pageSize: AUDIENCE_LEARNERS_PAGE_SIZE,
    search: debouncedSearch,
  });

  const pageRows: LearnerRow[] = React.useMemo(
    () => learners.map((learner) => ({ learner, inList: true })),
    [learners]
  );

  // TODO: The cohort fetch API does not return a total count. `filteredCount`
  // is an estimate based on whether a next page exists. Replace with a real
  // total once the API exposes one (or switch to cursor-based pagination).
  const pageCount = hasMore ? page + 1 : page;
  const filteredCount = hasMore
    ? (page + 1) * AUDIENCE_LEARNERS_PAGE_SIZE
    : (page - 1) * AUDIENCE_LEARNERS_PAGE_SIZE + learners.length;

  const setSearchQuery = React.useCallback((next: string) => {
    setSearchQueryRaw(next);
    setPage(1);
  }, []);

  return { searchQuery, setSearchQuery, page, setPage, pageCount, pageRows, filteredCount, loading, error };
};

export default useCohortLearnerList;
